import { Trash2 } from 'lucide-react'
import { useState } from 'react'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'

interface ReasonDialogProps {
  trigger: string
  /** Shows the trigger as a trash icon, with trigger as its name. */
  trashIcon?: boolean
  disabled?: boolean
  title: string
  description: string
  label: string
  confirmLabel: string
  /** Resolves to true once saved, which closes the dialog. */
  onConfirm: (text: string) => Promise<boolean>
}

/** A step that needs a few words from staff first, such as a question for the customer or a decline reason. */
export function ReasonDialog({
  trigger,
  trashIcon,
  disabled,
  title,
  description,
  label,
  confirmLabel,
  onConfirm,
}: ReasonDialogProps) {
  const [open, setOpen] = useState(false)
  const [text, setText] = useState('')
  const [saving, setSaving] = useState(false)

  async function confirm() {
    setSaving(true)
    const saved = await onConfirm(text.trim())
    setSaving(false)
    if (saved) {
      setOpen(false)
      setText('')
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trashIcon ? (
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            aria-label={trigger}
            title={trigger}
            disabled={disabled}
            className="text-destructive hover:bg-destructive/10"
          >
            <Trash2 className="size-4" />
          </Button>
        ) : (
          <Button type="button" variant="outline" disabled={disabled}>
            {trigger}
          </Button>
        )}
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-2">
          <Label htmlFor="endorsement-reason">{label}</Label>
          <Textarea
            id="endorsement-reason"
            rows={3}
            value={text}
            disabled={saving}
            onChange={(event) => setText(event.target.value)}
          />
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            disabled={!text.trim()}
            onClick={() => void confirm()}
            loading={saving ? 'Saving…' : undefined}
          >
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
